import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './CustomAnalysisPageManager.css';

const emptyForm = {
  id: '',
  routeName: '',
  allowedEmailsText: '',
  htmlFile: null,
};

const getAuthToken = () => localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

const normalizeRouteInput = (value) => (
  value
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
);

const formatTimestamp = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString();
};

const CustomAnalysisPageManager = ({ showNotification }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedFileName, setSelectedFileName] = useState('');

  const isEditing = Boolean(form.id);

  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()),
    [pages]
  );

  const loadPages = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      const response = await axios.get('/api/admin/custom-analysis-pages', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setPages(response.data.pages || []);
    } catch (error) {
      console.error('Failed to load custom analysis pages:', error);
      showNotification(error.response?.data?.message || '커스텀 분석 페이지 목록을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedFileName('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.routeName) {
      showNotification('라우트 이름을 입력해주세요.', 'error');
      return;
    }

    if (!isEditing && !form.htmlFile) {
      showNotification('새 페이지를 만들 때는 HTML 파일이 필요합니다.', 'error');
      return;
    }

    try {
      setSaving(true);
      const token = getAuthToken();
      const payload = new FormData();
      payload.append('routeName', form.routeName);
      payload.append('allowedEmails', form.allowedEmailsText);

      if (form.htmlFile) {
        payload.append('htmlFile', form.htmlFile);
      }

      const requestConfig = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = isEditing
        ? await axios.put(`/api/admin/custom-analysis-pages/${form.id}`, payload, requestConfig)
        : await axios.post('/api/admin/custom-analysis-pages', payload, requestConfig);

      showNotification(response.data.message || '커스텀 분석 페이지가 저장되었습니다.', 'success');
      resetForm();
      await loadPages();
    } catch (error) {
      console.error('Failed to save custom analysis page:', error);
      showNotification(error.response?.data?.message || '커스텀 분석 페이지를 저장하지 못했습니다.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (page) => {
    setForm({
      id: page.id,
      routeName: page.route_name,
      allowedEmailsText: (page.allowed_emails || []).join('\n'),
      htmlFile: null,
    });
    setSelectedFileName(page.original_file_name || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (page) => {
    const confirmed = window.confirm(`"${page.route_name}" 커스텀 페이지를 삭제할까요?`);
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(page.id);
      const token = getAuthToken();
      const response = await axios.delete(`/api/admin/custom-analysis-pages/${page.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      showNotification(response.data.message || '커스텀 분석 페이지가 삭제되었습니다.', 'success');

      if (form.id === page.id) {
        resetForm();
      }

      await loadPages();
    } catch (error) {
      console.error('Failed to delete custom analysis page:', error);
      showNotification(error.response?.data?.message || '커스텀 분석 페이지를 삭제하지 못했습니다.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="sub-page-content">
      <div className="custom-analysis-header">
        <div>
          <h2>🧩 커스텀 분석 페이지</h2>
          <p>업로드한 HTML 파일을 `vegaxholdings.com/analysis/&lt;route&gt;` 로 연결합니다.</p>
        </div>
        <button
          type="button"
          className="refresh-button"
          onClick={loadPages}
          disabled={loading}
        >
          {loading ? '새로고침 중...' : '새로고침'}
        </button>
      </div>

      <div className="custom-analysis-note">
        <strong>라우팅 규칙</strong>
        <span>기본 경로는 `/analysis/&lt;route&gt;` 입니다.</span>
        <span>같은 경로가 나중에 사이트 로직 페이지로 사용되면 `/analysis/custom/&lt;route&gt;` fallback 경로로 계속 접근할 수 있습니다.</span>
        <span>허용 이메일을 비워두면 공개 페이지, 입력하면 해당 이메일만 문서 접근 화면에서 바로 들어갈 수 있습니다.</span>
        <span>이 접근은 사용자 관리와 연결되지 않습니다. 이메일만 허용 목록에 있으면 됩니다.</span>
      </div>

      <form className="custom-analysis-form" onSubmit={handleSubmit}>
        <div className="custom-analysis-form-grid">
          <label>
            Route Name
            <input
              type="text"
              value={form.routeName}
              onChange={(event) => setForm((current) => ({
                ...current,
                routeName: normalizeRouteInput(event.target.value),
              }))}
              placeholder="example-report"
              maxLength={64}
            />
          </label>

          <label>
            Allowed Emails
            <textarea
              value={form.allowedEmailsText}
              onChange={(event) => setForm((current) => ({
                ...current,
                allowedEmailsText: event.target.value,
              }))}
              placeholder={'user1@company.com\nuser2@company.com'}
              rows={5}
            />
          </label>

          <label>
            HTML File
            <input
              type="file"
              accept=".html,.htm,text/html"
              onChange={(event) => {
                const nextFile = event.target.files?.[0] || null;
                setForm((current) => ({
                  ...current,
                  htmlFile: nextFile,
                }));
                setSelectedFileName(nextFile?.name || '');
              }}
            />
            <small>
              {isEditing
                ? selectedFileName
                  ? `현재 파일: ${selectedFileName} (새 파일을 선택하지 않으면 기존 파일을 유지합니다.)`
                  : '새 HTML 파일을 선택하면 기존 파일을 교체합니다.'
                : '단일 HTML 파일을 업로드해주세요. 상대 경로 자산은 함께 업로드되지 않습니다.'}
            </small>
          </label>
        </div>

        <div className="custom-analysis-form-actions">
          <button type="submit" className="update-button" disabled={saving}>
            {saving ? '저장 중...' : isEditing ? '페이지 업데이트' : '페이지 생성'}
          </button>
          {isEditing && (
            <button type="button" className="cancel-button" onClick={resetForm} disabled={saving}>
              편집 취소
            </button>
          )}
        </div>
      </form>

      <div className="custom-analysis-list">
        <div className="custom-analysis-list-header">
          <h3>현재 바인딩</h3>
          <span>{sortedPages.length}개</span>
        </div>

        {loading ? (
          <div className="custom-analysis-empty">커스텀 분석 페이지 목록을 불러오는 중입니다...</div>
        ) : sortedPages.length === 0 ? (
          <div className="custom-analysis-empty">등록된 커스텀 분석 페이지가 없습니다.</div>
        ) : (
          <div className="custom-analysis-cards">
            {sortedPages.map((page) => (
              <div key={page.id} className="custom-analysis-card">
                <div className="custom-analysis-card-header">
                  <div>
                    <h4>{page.title || page.route_name}</h4>
                    <p>{page.route_name}</p>
                  </div>
                  <span className={`custom-analysis-badge ${(page.allowed_emails || []).length > 0 ? 'restricted' : 'public'}`}>
                    {(page.allowed_emails || []).length > 0 ? 'Restricted' : 'Public'}
                  </span>
                </div>

                <div className="custom-analysis-links">
                  <a href={`https://vegaxholdings.com${page.primary_path}`} target="_blank" rel="noreferrer">
                    Primary: {page.primary_path}
                  </a>
                  <a href={`https://vegaxholdings.com${page.fallback_path}`} target="_blank" rel="noreferrer">
                    Fallback: {page.fallback_path}
                  </a>
                </div>

                <div className="custom-analysis-meta">
                  <span>파일: {page.original_file_name || '-'}</span>
                  <span>업데이트: {formatTimestamp(page.updated_at)}</span>
                </div>

                <div className="custom-analysis-email-list">
                  {(page.allowed_emails || []).length > 0 ? (
                    page.allowed_emails.map((email) => (
                      <span key={email} className="custom-analysis-email-chip">{email}</span>
                    ))
                  ) : (
                    <span className="custom-analysis-public-text">이 페이지는 공개 접근입니다.</span>
                  )}
                </div>

                <div className="custom-analysis-actions">
                  <button type="button" onClick={() => startEdit(page)}>
                    편집
                  </button>
                  <button
                    type="button"
                    className="danger-button"
                    onClick={() => handleDelete(page)}
                    disabled={deletingId === page.id}
                  >
                    {deletingId === page.id ? '삭제 중...' : '삭제'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomAnalysisPageManager;
